import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Loader2 } from 'lucide-react';
import { getAgentRunTimeScore } from '../../../services/api';
import { publishedBandStyle } from '../../../services/trustBands';
import {
  Dial, DeductionRow, SectionLabel, Pill,
  PANEL_STYLE, factorName, evidenceLabel,
} from './trustScoreParts';

/**
 * RunTimeScorePanel — what a governed agent's runs have earned it.
 *
 * The counterpart to TrustScorePanel, drawn from the same parts and resolved through the same
 * publishedBandStyle(), so an operator reading a design-time score and a run-time score is reading
 * one instrument. What differs is the evidence, and the panel says so on its face rather than
 * leaving a reader to infer it from the number.
 *
 * No arithmetic happens here. The score, the band and the deduction ladder all arrive computed;
 * this renders them.
 *
 * @param {string} props.agentId  the governed agent
 */
export function RunTimeScorePanel({ agentId }) {
  const [score, setScore] = useState(null);
  const [state, setState] = useState('idle');

  const load = useCallback(async () => {
    if (!agentId) { setState('none'); return; }
    setState('loading');
    const res = await getAgentRunTimeScore(agentId);
    if (res?.ok === false) { setState('error'); return; }
    setScore(res?.data || res);
    setState('ready');
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const style = useMemo(() => publishedBandStyle(score?.score), [score?.score]);

  const ev = score?.evidence || {};
  const deductions = Array.isArray(score?.deductions) ? score.deductions : [];
  const rated = score?.rated === true;

  return (
    <div style={PANEL_STYLE}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Activity size={14} style={{ color: 'var(--v2-text-muted)' }} />
        <span style={{ fontSize: 'var(--v2-trust-title-size)',
          fontWeight: 'var(--v2-trust-title-weight)' }}>Trust Score</span>
        <span style={{ fontSize: 'var(--v2-trust-meta-size)', color: 'var(--v2-text-muted)' }}>
          earned by this agent&rsquo;s own runs
        </span>
        {state === 'loading' && (
          <Loader2 size={12} className="animate-spin"
            style={{ marginLeft: 'auto', color: 'var(--v2-text-muted)' }} />
        )}
      </div>

      {state === 'error' && (
        <div style={{ fontSize: 'var(--v2-trust-body-size)', color: 'var(--v2-danger)' }}>
          Couldn&rsquo;t load the run-time score. Try again in a moment.
        </div>
      )}

      {state === 'ready' && score && !rated && (
        <div style={{ fontSize: 'var(--v2-trust-body-size)', color: 'var(--v2-text-muted)',
          lineHeight: 1.6 }}>
          This agent has not run under protection yet, so there is nothing to score. A run-time
          score is earned, not assigned &mdash; it appears once its first protected actions have been
          evaluated.
        </div>
      )}

      {state === 'ready' && score && rated && (
        <>
          <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
            <Dial score={score.score} style={style} label="Run-time trust score" />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
                <Pill>{String(score.basisLabel || '').toUpperCase()}</Pill>
                {evidenceLabel(score.rung) && <Pill>{evidenceLabel(score.rung)}</Pill>}
                <Pill>CONFIDENCE {Number(score.confidence).toFixed(2)}</Pill>
              </div>
              <p style={{ fontSize: 'var(--v2-trust-body-size)',
                color: 'var(--v2-text-secondary)', lineHeight: 1.6, margin: 0 }}>
                Describes how this agent has <em>behaved</em> &mdash; whether its governed actions
                stayed inside the policy that binds it, across {ev.evaluations} evaluations over the
                last {ev.windowDays} days.
              </p>
            </div>
          </div>

          {deductions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SectionLabel>What lowered this score</SectionLabel>
              {deductions.map((d) => (
                <DeductionRow key={d.factorKey} name={factorName(d.factorKey)} points={d.points} />
              ))}
              {score.reconcileNote && (
                <p style={{ fontSize: 'var(--v2-trust-caption-size)',
                  color: 'var(--v2-text-muted)', lineHeight: 1.6, marginTop: 8 }}>
                  {score.reconcileNote}
                </p>
              )}
            </div>
          )}

          {deductions.length === 0 && (
            <div style={{ marginTop: 16, fontSize: 'var(--v2-trust-body-size)',
              color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
              Nothing has lowered this score. Every protected action in the window stayed inside
              policy.
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <SectionLabel>Evidence</SectionLabel>
            <EvidenceRow label="Evaluations" value={ev.evaluations} />
            <EvidenceRow label="Blocked" value={ev.blocks} />
            <EvidenceRow label="Warned" value={ev.warns} />
            <EvidenceRow label="Audited" value={ev.audits} />
            <EvidenceRow label="Window" value={`${ev.windowDays} days`} />
          </div>

          <p style={{ fontSize: 'var(--v2-trust-meta-size)', color: 'var(--v2-text-muted)',
            lineHeight: 1.6, marginTop: 14,
            paddingTop: 10, borderTop: '1px solid var(--v2-border-subtle)' }}>
            Scored on the published method, version {score.methodologyVersion}. The score and its
            confidence are read together: confidence is how far this agent&rsquo;s evidence has
            reached the {ev.evaluationsForFullConfidence} evaluations your scoring policy asks for
            before acting on the number.
          </p>
        </>
      )}
    </div>
  );
}

/** One line of the evidence record. Counts, not judgements. */
function EvidenceRow({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '5px 0',
      borderBottom: '1px solid var(--v2-border-subtle)' }}>
      <span style={{ fontSize: 'var(--v2-trust-row-size)', color: 'var(--v2-text-secondary)',
        flex: 1 }}>{label}</span>
      <span style={{ fontSize: 'var(--v2-trust-value-size)',
        fontWeight: 'var(--v2-trust-value-weight)', color: 'var(--v2-text-primary)',
        fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</span>
    </div>
  );
}

export default RunTimeScorePanel;
